import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSignUpStore } from "../store";
import { registerReq } from "@/api/services/auth";
import type { SignUpFormData } from "../store/types";
import { paths } from "@/navigation/paths";

type FieldErrors = Partial<Record<keyof SignUpFormData, string>>;

export const useSignUp = () => {
  const navigate = useNavigate();
  const [fieldsErrors, setFieldsErrors] = useState<FieldErrors>({});
  const { phone, setPhone, setLoading, setError, isLoading, errorMessage } =
    useSignUpStore();

  // Clear error when component mounts
  useEffect(() => {
    setError(null);
  }, [setError]);

  const formData: SignUpFormData = { phone };

  const onChangePhoneField = (value: string) => {
    setPhone(value);
    if (fieldsErrors.phone) {
      setFieldsErrors({ ...fieldsErrors, phone: undefined });
    }
  };

  const onSubmit = async (data: SignUpFormData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await registerReq({ phone: data.phone });

      if (response.data === "success") {
        // Navigate to OTP verification page and clear form
        navigate(paths.VERIFY_OTP, {
          state: { phone: data.phone, isSignUp: true },
        });
        // Clear the form after successful navigation
        useSignUpStore.getState().reset();
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Registration failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const setFieldErrorsHandler = (errors: FieldErrors) => {
    setFieldsErrors(errors);
  };

  const clearFieldErrorsHandler = () => {
    setFieldsErrors({});
  };

  return {
    formData,
    fieldsErrors,
    onSubmit,
    onChangePhoneField,
    setFieldErrorsHandler,
    clearFieldErrorsHandler,
    isLoading,
    errorMessage,
  };
};
